import { DateTime } from 'luxon';
import type { FC } from 'react';
import { ChatQuote } from 'react-bootstrap-icons';

import type { SentimentView } from '../../../../modules/interfaces/dashboard';
import { formatCount } from '../../../../modules/utils/dashboard';
import { WidgetCard } from './WidgetCard';

export interface SentimentCardProps {
  sentiment?: SentimentView | null;
  loading: boolean;
}

/** The pill always carries its label text — never color alone. */
const pillModifier = (label: string): string => {
  const normalized = label.toLowerCase();

  if (normalized.includes('positive')) {
    return 'dashboard-sentiment__pill--positive';
  }

  if (normalized.includes('negative')) {
    return 'dashboard-sentiment__pill--negative';
  }

  return 'dashboard-sentiment__pill--neutral';
};

const formatTimestamp = (value?: string): string | null => {
  if (!value) {
    return null;
  }

  const parsed = DateTime.fromISO(value);

  return parsed.isValid ? parsed.toFormat('d LLL yyyy') : null;
};

export const SentimentCard: FC<SentimentCardProps> = ({ sentiment, loading }) => {
  const updatedAt = formatTimestamp(sentiment?.lastProcessedTimestamp);

  return (
    <WidgetCard
      title="Guest Sentiment"
      loading={loading}
      // The API returns null until sentiment ingestion is built. That is the
      // normal case today, not an error and not a loading state.
      isEmpty={!loading && !sentiment}
      emptyIcon={<ChatQuote size={28} />}
      emptyMessage="This feature is still being built. Once it's live, it will summarize how guests are feeling about their stay."
      // Self-clearing: the badge disappears as soon as the API returns a
      // sentiment payload, so there is nothing to remember to remove later.
      headerAccessory={
        !loading && !sentiment ? (
          <span className="dashboard-badge dashboard-badge--pending">Coming soon</span>
        ) : undefined
      }
      placeholderHeight={120}
    >
      {sentiment && (
        <>
          <div className="dashboard-sentiment">
            <span className="dashboard-sentiment__score">
              {sentiment.score.toFixed(1)}
            </span>
            <span
              className={`dashboard-sentiment__pill ${pillModifier(sentiment.label)}`}
            >
              {sentiment.label}
            </span>
          </div>

          {sentiment.summary && (
            <p className="dashboard-sentiment__summary">{sentiment.summary}</p>
          )}

          <p className="dashboard-sentiment__meta">
            Based on {formatCount(sentiment.sampleSize)} responses
            {updatedAt ? ` · updated ${updatedAt}` : ''}
          </p>
        </>
      )}
    </WidgetCard>
  );
};
